import { Link, usePage } from "@inertiajs/react"
import { ClipboardList, Home, Receipt, ShoppingBag, Utensils } from "lucide-react"
import { WaiterCallButton } from "./waiter-call-button"
import type { DinerSummary } from "./types"
import { DINER_HOME_PATH } from "@/lib/diner-home"
import { cn } from "@/lib/utils"
import { t } from "@/lib/i18n"

type TabId = "home" | "menu" | "cart" | "orders" | "receipts"

type TabConfig = {
  id: TabId
  label: string
  href: string
  icon: typeof Home
  badgeKey?: "cartCount"
}

/**
 * The catalogue of diner destinations. Which of them are offered is decided by
 * the shell from the diner's context, never by this list — outside a restaurant
 * the menu and the bill do not exist, and the backend bounces both.
 *
 * The profile is not here: it hangs off the avatar in the top bar, on mobile and
 * desktop alike.
 */
export const USER_NAV_TABS: TabConfig[] = [
  { id: "home", label: t("ui.react.features.user.tab_bar.home"), href: DINER_HOME_PATH, icon: Home },
  { id: "menu", label: t("ui.react.features.user.tab_bar.menu"), href: "/dinein/menu", icon: Utensils },
  {
    id: "cart",
    label: t("ui.react.features.user.tab_bar.bill"),
    href: "/dinein/cart",
    icon: ShoppingBag,
    badgeKey: "cartCount",
  },
  {
    id: "orders",
    label: t("ui.react.features.user.tab_bar.orders"),
    href: "/dinein/orders",
    icon: ClipboardList,
  },
  {
    id: "receipts",
    label: t("ui.react.features.user.tab_bar.receipts"),
    href: "/receipts",
    icon: Receipt,
  },
]

const tabById = (id: TabId) => USER_NAV_TABS.find((tab) => tab.id === id) as TabConfig

/**
 * The bar only renders inside a restaurant, and there the visit is the whole
 * job: home and the order history left for the profile so the two remaining
 * destinations sit one per side, with the waiter-call slot on the exact center.
 */
export const TABS_BEFORE_WAITER_CALL = [tabById("menu")]
export const TABS_AFTER_WAITER_CALL = [tabById("cart")]

/**
 * Offered in the top bar when there is no visit, so both destinations have to
 * mean something without one.
 *
 * History is `/receipts`, not `/orders`. `/orders` is the current visit and
 * nothing else — the same rule `dinerProfileGroups` states for the hub. It used
 * to sit here because it answered without a visit too, by dropping its location
 * filter and returning every dish the diner had ever ordered anywhere; that was
 * the bug, not the feature. A receipt is also the row a diner remembers, where a
 * cross-visit list of individual dishes is a wall with no sense of when or where.
 */
export const NO_CONTEXT_NAV_TABS = [tabById("home"), tabById("receipts")]

/**
 * Desktop icon nav inside a restaurant. The cart is not here: it renders
 * separately so it can carry the count badge.
 */
export const CONTEXT_NAV_TABS = [tabById("menu")]

/**
 * Pickup destinations are per restaurant, because pickup is addressable: the
 * slug is the context, so the tabs cannot be module constants the way the
 * visit's are. Same two destinations, same order, same icons — a diner should
 * not have to learn a second bar.
 */
export function pickupNavTabs(slug: string): TabConfig[] {
  return [
    { ...tabById("menu"), href: `/r/${slug}` },
    { ...tabById("cart"), href: `/r/${slug}/cart` },
  ]
}

/**
 * Whether the shell offers the waiter call, and it takes the route as well as
 * the context.
 *
 * The call is an action of the room the diner is sitting in, so it belongs to
 * the screens of that room — `/dinein/*` — and nowhere else. It used to hang off
 * the context alone, which put a "call a waiter" button on the dashboard, the
 * receipts list and the profile: screens where nothing around it has anything to
 * do with the table, and where the diner is plainly not looking at one.
 *
 * `shellMode` is `visit` on exactly the `/dinein/*` routes, because
 * `User::VisitController` declares it and nothing else inherits that base — see
 * `docs/domains/user/features/diner-route-context.md`. So this reads the route
 * from the server's own contract instead of sniffing the path, which would go
 * stale the moment a route moves.
 *
 * The context half stands as it was: nobody to call without a table, and a
 * restaurant that switched the call off answers every request with a refusal, so
 * the slot collapses rather than offering a control that can only fail.
 */
export function waiterCallSlot({
  shellMode,
  diner,
  isPickup,
}: {
  shellMode: string | undefined
  diner: DinerSummary | undefined
  isPickup: boolean
}): { pending: boolean } | null {
  if (shellMode !== "visit") return null
  if (isPickup || !diner?.tableName || !diner.waiterCallEnabled) return null

  return { pending: Boolean(diner.waiterCallPending) }
}

type TabBarProps = {
  cartCount?: number
  /**
   * Dine-in with a table only. Pickup and table-less visits have nobody to
   * call, so the slot collapses and the four tabs share the full width.
   */
  waiterCall?: { pending: boolean } | null
  /** Overrides the visit's destinations. Pickup passes its own, scoped to the slug. */
  tabs?: { before: TabConfig[]; after: TabConfig[] }
  className?: string
}

export function isTabActive(currentPath: string, href: string) {
  if (href === "/") return currentPath === "/" || currentPath.startsWith("/dashboard")
  return currentPath === href || currentPath.startsWith(`${href}/`)
}

export function TabBar({ cartCount = 0, waiterCall = null, tabs, className }: TabBarProps) {
  const currentPath = usePage().url
  const before = tabs?.before ?? TABS_BEFORE_WAITER_CALL
  const after = tabs?.after ?? TABS_AFTER_WAITER_CALL

  const renderTab = (tab: TabConfig) => {
    const active = isTabActive(currentPath, tab.href)
    const Icon = tab.icon
    const badge = tab.badgeKey === "cartCount" ? cartCount : 0

    return (
      <Link
        key={tab.id}
        href={tab.href}
        data-testid={`tab-bar-${tab.id}`}
        data-active={active ? "true" : "false"}
        aria-current={active ? "page" : undefined}
        className={cn(
          "relative flex min-h-16 min-w-0 flex-1 flex-col items-center justify-center gap-0.5 px-1 text-xs font-medium transition-colors",
          // `text-brand-ink`, not `text-primary`: this color cascades onto the
          // label span below, which is real text at text-xs. --primary now
          // resolves to chile-600, which measures 3.77:1 on the dark theme's
          // page and fails AA for small text — see DESIGN.md's Contrast-Step
          // Rule. brand-ink (chile-700 / chile-400) is the token that carries
          // brand-colored small text and stays AA in both themes.
          active ? "text-brand-ink" : "text-muted-foreground"
        )}
      >
        <Icon className="h-5 w-5 shrink-0" />
        <span className="max-w-full truncate">{tab.label}</span>
        {badge > 0 && (
          <span className="absolute top-2 right-[calc(50%-1.5rem)] flex h-5 min-w-5 items-center justify-center rounded-full border-2 border-background bg-brand-solid px-1 text-xs font-bold tabular-nums text-brand-foreground">
            {badge}
          </span>
        )}
      </Link>
    )
  }

  return (
    <nav
      data-testid="tab-bar"
      aria-label={t("ui.navigation.navigation_menu")}
      className={cn(
        "flex shrink-0 items-stretch border-t bg-background pb-[env(safe-area-inset-bottom)]",
        className
      )}
    >
      {before.map(renderTab)}

      {waiterCall && (
        <div
          className="flex min-h-16 w-16 shrink-0 items-center justify-center"
          data-testid="tab-bar-waiter-call"
        >
          <WaiterCallButton
            testId="tab-bar-waiter-call-button"
            pending={waiterCall.pending}
          />
        </div>
      )}

      {after.map(renderTab)}
    </nav>
  )
}
